import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { getEventById } from '../../lib/api/events';
import { getApplicationsByEventDecrypted } from '../../lib/api/applications';
import type { ApplicationWithDetails, EventWithDetails } from '../../types/database';
import { captureError } from '../../lib/capture-error';
import {
  NO_OPTION,
  buildGroupedByChurch,
  buildGroupedByOption,
  filterApplications,
  formatApplicantName,
  formatOptionLabel,
  formatStatusLabel,
  formatSubmittedAt,
  getSelectedOptionsForApp,
  parseApplicationsPrintSearchParams,
  type ChurchApplicationGroup,
  type OptionApplicationGroup,
} from '../../lib/applications-report';
import './AdminApplicationsPrintPage.css';

function formatOptionsText(
  app: ApplicationWithDetails,
  optionById: Map<string, import('../../types/database').EventSignupOption>,
  hasSignupOptions: boolean
): string {
  if (!hasSignupOptions) return '';
  const options = getSelectedOptionsForApp(app, optionById);
  if (options.length === 0) return '—';
  return options.map((o) => formatOptionLabel(o)).join('; ');
}

function ApplicantsPrintTable({
  applications,
  hasSignupOptions,
  optionById,
}: {
  applications: ApplicationWithDetails[];
  hasSignupOptions: boolean;
  optionById: Map<string, import('../../types/database').EventSignupOption>;
}) {
  return (
    <table className="applicants-print-table">
      <thead>
        <tr>
          <th>#</th>
          <th>Applicant</th>
          <th>Contact</th>
          <th>Church</th>
          {hasSignupOptions && <th>Registration</th>}
          <th>Status</th>
          <th>Submitted</th>
        </tr>
      </thead>
      <tbody>
        {applications.map((app, index) => {
          const profile = app.attendee_profile;
          const church = profile?.church_info?.church_name || '—';
          return (
            <tr key={app.id}>
              <td>{index + 1}</td>
              <td>
                <strong>{formatApplicantName(app)}</strong>
              </td>
              <td>
                {profile?.email || '—'}
                {profile?.cell_phone ? (
                  <>
                    <br />
                    {profile.cell_phone}
                  </>
                ) : null}
              </td>
              <td>{church}</td>
              {hasSignupOptions && <td>{formatOptionsText(app, optionById, hasSignupOptions)}</td>}
              <td>{formatStatusLabel(app.status)}</td>
              <td>{formatSubmittedAt(app)}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function ChurchSection({
  group,
  hasSignupOptions,
  optionById,
}: {
  group: ChurchApplicationGroup;
  hasSignupOptions: boolean;
  optionById: Map<string, import('../../types/database').EventSignupOption>;
}) {
  return (
    <section className="print-section">
      <h3 className="print-section-title">
        {group.churchName}
        <span className="print-count">({group.applications.length})</span>
      </h3>
      <ApplicantsPrintTable
        applications={group.applications}
        hasSignupOptions={hasSignupOptions}
        optionById={optionById}
      />
    </section>
  );
}

function OptionSection({
  group,
  groupByChurch,
  hasSignupOptions,
  optionById,
}: {
  group: OptionApplicationGroup;
  groupByChurch: boolean;
  hasSignupOptions: boolean;
  optionById: Map<string, import('../../types/database').EventSignupOption>;
}) {
  return (
    <section className="print-section print-option-group">
      <h2 className="print-option-title">
        {group.optionLabel}
        <span className="print-count">({group.applications.length})</span>
      </h2>
      {groupByChurch && group.churchGroups ? (
        group.churchGroups.map((churchGroup) => (
          <ChurchSection
            key={churchGroup.churchName}
            group={churchGroup}
            hasSignupOptions={hasSignupOptions}
            optionById={optionById}
          />
        ))
      ) : (
        <ApplicantsPrintTable
          applications={group.applications}
          hasSignupOptions={hasSignupOptions}
          optionById={optionById}
        />
      )}
    </section>
  );
}

const AdminApplicationsPrintPage: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const [searchParams] = useSearchParams();
  const filters = useMemo(
    () => parseApplicationsPrintSearchParams(searchParams),
    [searchParams]
  );

  const [event, setEvent] = useState<EventWithDetails | null>(null);
  const [applications, setApplications] = useState<ApplicationWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    if (!eventId) return;

    try {
      setLoading(true);
      setError(null);
      const [eventData, applicationsData] = await Promise.all([
        getEventById(eventId),
        getApplicationsByEventDecrypted(eventId),
      ]);
      setEvent(eventData);
      setApplications(applicationsData);
    } catch (err) {
      setError('Failed to load applications');
      captureError(err);
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    document.body.classList.add('applicants-print-route');
    return () => document.body.classList.remove('applicants-print-route');
  }, []);

  const signupOptions = useMemo(() => event?.signup_options ?? [], [event?.signup_options]);
  const hasSignupOptions = signupOptions.length > 0;
  const optionById = useMemo(
    () => new Map(signupOptions.map((option) => [option.id, option])),
    [signupOptions]
  );

  const filteredApplications = useMemo(
    () => filterApplications(applications, filters),
    [applications, filters]
  );

  const groupedByChurch = useMemo(
    () => buildGroupedByChurch(filteredApplications, filters.groupByChurch, filters.groupByOption),
    [filteredApplications, filters.groupByChurch, filters.groupByOption]
  );

  const groupedByOption = useMemo(
    () =>
      buildGroupedByOption(
        filteredApplications,
        signupOptions,
        filters.groupByOption,
        filters.groupByChurch
      ),
    [filteredApplications, signupOptions, filters.groupByOption, filters.groupByChurch]
  );

  const filterSummary = useMemo(() => {
    const parts: string[] = [];
    if (filters.status !== 'all') {
      parts.push(`Status: ${formatStatusLabel(filters.status)}`);
    }
    if (filters.searchQuery.trim()) {
      parts.push(`Search: "${filters.searchQuery.trim()}"`);
    }
    if (filters.groupByOption) parts.push('Grouped by registration option');
    if (filters.groupByChurch) parts.push('Grouped by church');
    if (parts.length === 0) parts.push('All applicants');
    return parts.join(' · ');
  }, [filters]);

  if (loading) {
    return (
      <div className="applicants-print-page">
        <p className="text-muted">Loading report…</p>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="applicants-print-page">
        <p className="text-danger">Event not found.</p>
        <Link to="/admin">Back to admin</Link>
      </div>
    );
  }

  return (
    <div className="applicants-print-page">
      <div className="print-toolbar no-print">
        <button type="button" className="btn btn-primary" onClick={() => window.print()}>
          <i className="fas fa-print me-2" aria-hidden />
          Print
        </button>
        <Link to={`/admin/events/${eventId}/applications`} className="btn btn-outline-secondary">
          Back to applications
        </Link>
      </div>

      <header className="print-header">
        <h1 className="print-title">{event.title}</h1>
        <p className="print-subtitle">Applicant report</p>
        <p className="print-meta">
          Generated {new Date().toLocaleString('en-US', { dateStyle: 'full', timeStyle: 'short' })}
        </p>
        <p className="print-meta">
          Showing {filteredApplications.length} of {applications.length} applicants · {filterSummary}
        </p>
      </header>

      {error && (
        <div className="alert alert-danger no-print" role="alert">
          {error}
        </div>
      )}

      {filteredApplications.length === 0 ? (
        <p className="print-empty">No applicants match the current filters.</p>
      ) : groupedByOption && groupedByOption.length > 0 ? (
        groupedByOption.map((optionGroup) => (
          <OptionSection
            key={optionGroup.optionId ?? NO_OPTION}
            group={optionGroup}
            groupByChurch={filters.groupByChurch}
            hasSignupOptions={hasSignupOptions}
            optionById={optionById}
          />
        ))
      ) : groupedByChurch && groupedByChurch.length > 0 ? (
        groupedByChurch.map((churchGroup) => (
          <ChurchSection
            key={churchGroup.churchName}
            group={churchGroup}
            hasSignupOptions={hasSignupOptions}
            optionById={optionById}
          />
        ))
      ) : (
        <section className="print-section">
          <ApplicantsPrintTable
            applications={filteredApplications}
            hasSignupOptions={hasSignupOptions}
            optionById={optionById}
          />
        </section>
      )}
    </div>
  );
};

export default AdminApplicationsPrintPage;
